import type { LaunchMentionResult } from '@reflex/plugin-api';
import type { ResourceSize } from '@runloop/reflex-contract';
import type { DevboxCapabilityId } from '@reflex/shared';
import type { AgentPersona } from '@reflex/plugin-agent-persona/shared/types';

/**
 * Concrete action the launch dialog performs for a plugin
 * {@link LaunchMentionResult}. Mirrors the result union but resolves the
 * persona id to an actual persona (or `noop` when it is not found) so the
 * dialog never dispatches against a missing persona.
 */
export type MentionApplyAction =
  | { type: 'apply-persona'; persona: AgentPersona }
  | { type: 'set-attachment'; id: string; pluginName: string; config: unknown }
  | { type: 'reference-agent-run'; agentId: string; title: string }
  | { type: 'reference-context'; contextKind: string; id: string; title: string }
  | { type: 'set-sandbox'; resourceSize?: ResourceSize; capabilityId?: DevboxCapabilityId }
  | { type: 'custom'; run: () => void }
  | { type: 'noop' };

/**
 * Pure mapping from a plugin `LaunchMentionResult` to the concrete action the
 * launch dialog performs. Extracted from the dialog so the routing — including
 * the "persona id not found" guard — is unit-testable without rendering.
 */
export function resolveMentionApply(
  result: LaunchMentionResult,
  personas: AgentPersona[],
): MentionApplyAction {
  switch (result.kind) {
    case 'apply-persona': {
      const persona = personas.find((p) => p.id === result.personaId);
      return persona ? { type: 'apply-persona', persona } : { type: 'noop' };
    }
    case 'set-attachment':
      return {
        type: 'set-attachment',
        id: result.attachmentId,
        pluginName: result.pluginName,
        config: result.config,
      };
    case 'reference-agent-run':
      return { type: 'reference-agent-run', agentId: result.agentId, title: result.title };
    case 'reference-context':
      return {
        type: 'reference-context',
        contextKind: result.contextKind,
        id: result.id,
        title: result.title,
      };
    case 'set-sandbox':
      return {
        type: 'set-sandbox',
        resourceSize: result.resourceSize,
        capabilityId: result.capabilityId,
      };
    case 'custom':
      return { type: 'custom', run: result.run };
  }
}
